import type { SprintProgress, TicketProgress } from '@shared/schemas';
import { sprintProgressInDays } from '@/lib/sprint-meta';
import { UiGlyph } from '@/components/UiGlyph';

/**
 * Q-1 «День N / 7» Sprint header chip · Sprint.tsx top meta line.
 *
 * Renders nothing если sprint not yet started (no startedAt and no ticket
 * fallback recordable). Composite consumer of `sprintProgressInDays` helper
 * @ cfa3c1a · accepts both sprint progress + ticket progress collection для
 * fallback resolution per CHAT_2B Q-1 graceful-degrade.
 *
 * `total` defaults к 7 (typical sprint length) · override для S0 onboarding
 * (3 days) · S18 finale (custom).
 */
export interface SprintDayHeaderProps {
  sprint: Pick<SprintProgress, 'startedAt'> | undefined;
  /** Tickets in this sprint (для MIN fallback if sprint.startedAt absent). */
  ticketsInSprint: Array<Pick<TicketProgress, 'startedAt'>>;
  /** Total days в sprint · default 7. */
  total?: number;
  /** Override «now» reference. */
  now?: Date;
}

export function SprintDayHeader({
  sprint,
  ticketsInSprint,
  total = 7,
  now,
}: SprintDayHeaderProps) {
  const { dayNumber } = sprintProgressInDays(sprint, ticketsInSprint, now);
  if (dayNumber === null) return null;

  // Clamp display к total · sprint can run «over time» but UI shows max total
  const displayDay = Math.min(dayNumber, total);
  const overTime = dayNumber > total;

  return (
    <span
      className={`sprint-day-header${overTime ? ' sprint-day-header-overtime' : ''}`}
      role="status"
      aria-label={`День ${displayDay} из ${total}${overTime ? ' (переработка)' : ''}`}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 4,
        padding: '2px 10px',
        fontSize: 'var(--font-sm, 13px)',
        borderRadius: 14,
        background: overTime ? 'var(--surface-warning, #fff4e0)' : 'var(--surface-2)',
        color: overTime ? 'var(--ink-warning, #8c4a00)' : 'var(--ink)',
        border: `1px solid var(--border-strong)`,
        whiteSpace: 'nowrap',
      }}
    >
      <UiGlyph name="calendar" size={14} />
      День {displayDay} / {total}
      {overTime && <span aria-hidden="true">· +{dayNumber - total}</span>}
    </span>
  );
}
